import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// ページコンポーネント
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UploadPage from './pages/UploadPage';
import TranscriptionPage from './pages/TranscriptionPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminTranscriptionsPage from './pages/AdminTranscriptionsPage';
import AdminRevenuePage from './pages/AdminRevenuePage';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import PlanChangedPage from './pages/PlanChangedPage';
import BillingCancelPage from './pages/BillingCancelPage';
import LegalPage from './pages/LegalPage';
import TermsSimplePage from './pages/TermsSimplePage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import PricingPage from './pages/PricingPage';
import GuidePage from './pages/GuidePage';
import ComingSoonPage from './pages/ComingSoonPage';
import MaintenancePage from './pages/MaintenancePage';
import ScrollToTop from './components/ScrollToTop';
import { useMaintenance } from './hooks/queries/useMaintenance';
import { COMING_SOON, BILLING_ENABLED } from './config/featureFlags';

function AppRoutes() {
  const { user, loading } = useAuth();
  const maintenance = useMaintenance();

  // 機能ページに被せるゲート。null なら素通し。
  // メンテを先に見るのは、正式開始後の運用ではメンテのほうが緊急度が高いため。
  // 管理者は通す（メンテ明け前に処理状況の確認・動作検証ができるように）。
  const gate =
    maintenance.active && !user?.is_admin ? (
      <MaintenancePage endsAt={maintenance.endsAt} message={maintenance.message} />
    ) : COMING_SOON ? (
      <ComingSoonPage />
    ) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl">読み込み中...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* 常時公開（正式開始前も表示）: トップ・法的ページ・サポート・サービス紹介
          /pricing・/guide は集客用の紹介ページのため、COMING_SOON 中も素通しで見せる。
          /terms-simple は法的ページの補助資料のため、本体3文書と同じく常時公開にする。 */}
      <Route path="/" element={<HomePage user={user} />} />
      <Route path="/terms" element={<LegalPage type="terms" />} />
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      <Route path="/commerce" element={<LegalPage type="commerce" />} />
      <Route path="/terms-simple" element={<TermsSimplePage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/guide" element={<GuidePage />} />

      {/* 機能ページ: メンテナンス中／正式サービス開始前（COMING_SOON）はゲート表示に差し替え */}
      <Route
        path="/login"
        element={
          gate ?? (user ? <Navigate to="/dashboard" /> : <LoginPage />)
        }
      />
      <Route
        path="/dashboard"
        element={
          gate ?? (user ? <DashboardPage user={user} /> : <Navigate to="/login" />)
        }
      />
      <Route
        path="/upload"
        element={
          gate ?? (user ? <UploadPage user={user} /> : <Navigate to="/login" />)
        }
      />
      <Route
        path="/transcription/:id"
        element={
          gate ?? (user ? <TranscriptionPage user={user} /> : <Navigate to="/login" />)
        }
      />
      <Route
        path="/profile"
        element={
          gate ?? (user ? <ProfilePage user={user} /> : <Navigate to="/login" />)
        }
      />
      <Route
        path="/billing"
        element={
          gate ?? (!BILLING_ENABLED ? <Navigate to="/dashboard" /> : user ? <BillingPage user={user} /> : <Navigate to="/login" />)
        }
      />
      <Route
        path="/billing/success"
        element={gate ?? (!BILLING_ENABLED ? <Navigate to="/dashboard" /> : user ? <BillingSuccessPage /> : <Navigate to="/login" />)}
      />
      <Route
        path="/billing/plan-changed"
        element={gate ?? (!BILLING_ENABLED ? <Navigate to="/dashboard" /> : user ? <PlanChangedPage /> : <Navigate to="/login" />)}
      />
      <Route
        path="/billing/cancel"
        element={gate ?? (!BILLING_ENABLED ? <Navigate to="/dashboard" /> : user ? <BillingCancelPage /> : <Navigate to="/login" />)}
      />
      <Route
        path="/admin"
        element={
          gate ?? (user?.is_admin ? <AdminPage user={user} /> : <Navigate to="/dashboard" />)
        }
      />
      <Route
        path="/admin/users"
        element={
          gate ?? (user?.is_admin ? <AdminUsersPage user={user} /> : <Navigate to="/dashboard" />)
        }
      />
      <Route
        path="/admin/transcriptions"
        element={
          gate ?? (user?.is_admin ? <AdminTranscriptionsPage user={user} /> : <Navigate to="/dashboard" />)
        }
      />
      <Route
        path="/admin/revenue"
        element={
          gate ?? (user?.is_admin ? <AdminRevenuePage user={user} /> : <Navigate to="/dashboard" />)
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AuthProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
